import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { Case, Default, Switch, When } from 'react-if'
import Sticky from 'react-stickynode'
import { Breadcrumb } from '@/atoms/Breadcrumb'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { projectTypes } from '@/constants/projectTypes'
import { MediaCard } from '@/molecules/MediaCard'
import { TIMELINE_ROLLUP_INTERVAL_MILLIS } from '@/organisms/AngelPlayer'
import { CollectiblesPlayer, PlayerMomentsProvider } from '@/organisms/CollectiblesPlayer'
import {
  CollectiblesPlayerEngagementProvider,
  EngagementDetail,
} from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { OneToManyContentSharingModal } from '@/organisms/CollectiblesPlayer/GuildContentLinking'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { ShareSection } from '@/organisms/ShareSection'
import { WatchEpisodeHero } from '@/organisms/WatchEpisodeHero'
import { getWebClient } from '@/services/ApolloClient'
import { useBotDetection } from '@/services/BotDetection'
import { useGuildMember } from '@/services/GuildUserService'
import { SerializedSkipCategoryPreference } from '@/services/ParentalControlsService'
import { useSkipCategoryPreferences } from '@/services/ParentalControlsService/useSkipCategoryPreferences'
import { isInGuildAccess } from '@/services/ProjectPhaseService'
import {
  Episode,
  GET_EPISODE_AND_USER_WATCH_DATA_QUERY,
  Project,
  ProjectType,
  useGetNextContent,
  useGetWatchableAvailabilityByGuid,
} from '@/services/ProjectsService'
import { ContentEpisode } from '@/services/ProjectsService/types'
import { ShopifyConfiguration } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { useUser } from '@/services/UserService'
import { ContentSeasonEdge, GetEpisodeAndUserWatchDataQuery, Moment } from '@/types/codegen-federation'
import { processContentEpisodesToMediasWithFiltering } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDefined } from '@/utils/primitives/generics'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { PillTabItem, PillTabs } from '@/views/WatchProjectTabsView/PillTabs'
import { Frame } from '../AccountView/tabs/CollectiblesTab/types'
import { createCompatibleEpisodeResponse } from './utils'

export interface WatchEpisodeViewProps {
  autoplay?: boolean
  defaultMoment?: Maybe<Moment>
  defaultEngagementDetail?: Maybe<EngagementDetail>
  episode: Episode
  episodeId: string
  hasPayItForward?: boolean
  projectName: string
  projectSlug: string
  projectType: ProjectType
  publisherName?: string
  skipAds?: boolean
  skipAttribution?: boolean
  shopifyStore?: ShopifyConfiguration | null
  userStoryId?: string
  frame?: Frame
  isGuildMember?: boolean
  isSellingTickets?: boolean
  canViewUnauthed?: boolean
  userId?: string | null
  parentalControlsTotal?: number
  showParentalControls?: boolean
  initialPreferences?: SerializedSkipCategoryPreference[]
  contentEpisode?: ContentEpisode
}

export const WatchEpisodeView: React.FC<WatchEpisodeViewProps> = ({
  autoplay,
  defaultMoment,
  defaultEngagementDetail,
  episode,
  episodeId,
  projectName,
  projectSlug,
  projectType,
  skipAds,
  skipAttribution,
  hasPayItForward,
  shopifyStore,
  userStoryId,
  frame,
  isGuildMember = false,
  isSellingTickets = false,
  canViewUnauthed = false,
  userId: userIdFromProps,
  parentalControlsTotal = 0,
  showParentalControls = false,
  initialPreferences = [],
  contentEpisode,
}) => {
  const { isLoggedIn, userId: userIdFromHook } = useUser()
  const userId = userIdFromProps !== undefined ? userIdFromProps : userIdFromHook
  const client = getWebClient()
  const { isBot } = useBotDetection()
  const { asPath } = useSafeRouter()
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { guildPermissions } = useGuildMember()
  const [stickyTop, setStickyTop] = useState(86)
  const containerRef = useRef<HTMLDivElement>(null)
  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const [activeSeason, setActiveSeason] = useState<ContentSeasonEdge | undefined>(undefined)
  const searchParams = useSearchParams()
  const [showShareModal, setShowShareModal] = useState(searchParams.has('share'))
  const { watchableContent } = useGetWatchableAvailabilityByGuid(episodeId, isLoggedIn, client)
  const {
    preferences: clientPreferences,
    loading: preferencesLoading,
    updateSkipPreference,
    refetch: refetchPreferences,
  } = useSkipCategoryPreferences({ lang: locale, contentId: episodeId })
  const { nextContent } = useGetNextContent({ contentGuid: contentEpisode?.id })

  const preferences = preferencesLoading ? initialPreferences : clientPreferences
  const hasParentalControlsAvailable = showParentalControls && parentalControlsTotal > 0

  const urlRequestsParentalControlsModal = searchParams.has('show-parental-controls')
  const showInitialParentalControlsModal = hasParentalControlsAvailable && urlRequestsParentalControlsModal
  const [showParentalControlsModal, setShowParentalControlsModal] = useState(showInitialParentalControlsModal)
  const shouldAutoplay = autoplay && !searchParams.has('share') && !showInitialParentalControlsModal

  useEffect(() => {
    if (showInitialParentalControlsModal) {
      setShowParentalControlsModal(true)
    }
  }, [showInitialParentalControlsModal])

  useEffect(() => {
    if (showParentalControlsModal) {
      refetchPreferences()
    }
  }, [showParentalControlsModal, refetchPreferences])

  const {
    loading,
    data,
    refetch: refetchEpisode,
  } = useQuery<GetEpisodeAndUserWatchDataQuery>(GET_EPISODE_AND_USER_WATCH_DATA_QUERY, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      guid: episodeId,
      projectSlug: projectSlug,
      authenticated: Boolean(userId),
      reactionsRollupInterval: TIMELINE_ROLLUP_INTERVAL_MILLIS,
    },
    errorPolicy: 'all',
  })

  const handleSaveAndWatch = () => {
    setShowParentalControlsModal(false)
    refetchEpisode()
  }

  const merchAnalytics = useMemo(() => {
    return { context: 'watch-episodes', content_id: episodeId, program: projectSlug }
  }, [episodeId, projectSlug])

  useEffect(() => {
    // Handle new Content API structure where seasons are in project.title.seasons
    if (data?.project?.title?.__typename === 'ContentSeries' && data.project.title.seasons?.edges) {
      const contentSeasons = data.project.title.seasons.edges.filter(isDefined)
      const currentEpisodeSeasonNumber = data.episode?.seasonNumber

      const selectedSeason = contentSeasons.find((edge) => {
        return edge?.node?.seasonNumber === currentEpisodeSeasonNumber
      })

      setActiveSeason((selectedSeason || contentSeasons[0] || undefined) as ContentSeasonEdge | undefined)
    }
  }, [data])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => setStickyTop(mq.matches ? 40 : 10)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (containerRef.current && typeof window !== 'undefined') {
      const containerTop = containerRef.current.getBoundingClientRect().top + window.scrollY
      const currentScroll = window.scrollY
      const stickyOffset = stickyTop

      // If we're scrolled past the container, scroll back to it
      if (currentScroll > containerTop) {
        window.scrollTo({
          top: containerTop - stickyOffset,
          behavior: 'smooth',
        })
      }
    }
  }, [activeSeason?.node?.id, stickyTop])

  useEffect(() => {
    writeToSessionStorage('pageSlug', 'watch-episode')
    if (projectSlug) writeToSessionStorage('projectSlugs', projectSlug)
  }, [projectSlug])

  const showPlayer = isLoggedIn || isBot
  const isSeriesOrPodcast = Boolean(projectType === projectTypes.series || projectType === projectTypes.podcast)

  // Process episodes for the active season
  const medias = useMemo(() => {
    if (!isSeriesOrPodcast || !activeSeason?.node?.episodes?.edges) return []

    return processContentEpisodesToMediasWithFiltering(
      activeSeason.node.episodes.edges.filter(isDefined),
      {
        projectName,
        projectType,
        trailerText: t('trailer', 'Trailer'),
        locale,
        selectedSeasonId: activeSeason.node.id || '',
        isGuildMember,
      },
      false, // isGeoblockedProject - not available in this context
      (data?.project ?? undefined) as Project | undefined,
    )
  }, [activeSeason, projectName, projectType, t, locale, isGuildMember, data?.project, isSeriesOrPodcast])

  // Build season tabs for the pill selector
  const seasonTabs: PillTabItem[] = useMemo(() => {
    if (!data?.project?.title?.__typename || data.project.title.__typename !== 'ContentSeries') return []

    const edges = data.project.title.seasons?.edges ?? []
    return edges
      .filter(isDefined)
      .map((season) => {
        const id = season?.node?.id ?? ''
        const count = season?.node?.episodes?.edges?.length ?? 0
        return {
          id,
          primary: `${t('season', 'Season')} ${season?.node?.seasonNumber}`,
          secondary: `${count} ${t('episodes', 'Episodes')}`,
        }
      })
      .filter((it) => Boolean(it.id))
  }, [data, t])

  const isUnavailable = useMemo(() => {
    return !data?.episode?.unavailableReason
      ? false
      : Boolean(
          watchableContent &&
            !data?.episode?.isTrailer &&
            (watchableContent.watchableReasons?.length === 0 ||
              watchableContent?.watchableAvailabilityStatus === 'WATCHABLE_AT'),
        )
  }, [data, watchableContent])
  const isLoginRequired = canViewUnauthed ? false : !userId && !episode.isTrailer
  const isEpisodeEarlyAccess = useMemo(() => {
    return isInGuildAccess(contentEpisode)
  }, [contentEpisode])

  const isGeoBlocked = useMemo(() => {
    const episodeGeoblocked = data?.episode?.unavailableReason === 'GEOBLOCKED'
    let projectRegionBlocked = false

    // Check different title types for watchableAvailabilityStatus
    if (data?.project?.title) {
      if (data.project.title.__typename === 'ContentMovie' || data.project.title.__typename === 'ContentSpecial') {
        projectRegionBlocked = data.project.title.watchableAvailabilityStatus === 'REGION_BLOCKED'
      }
    }

    return episodeGeoblocked || projectRegionBlocked
  }, [data?.episode?.unavailableReason, data?.project?.title])
  const canSeeGuildShareButton = Boolean(data?.project?.title?.isGuildShareAvailable) && isGuildMember
  const hasSourceUrl = Boolean(data?.episode?.source?.url || contentEpisode?.url)
  const showEpisodeHero = Boolean(
    !hasSourceUrl || isUnavailable || (isEpisodeEarlyAccess && !isLoggedIn) || isGeoBlocked || isLoginRequired,
  )

  return (
    <div
      className={classNames('relative w-full overflow-x-hidden bg-core-gray-950 text-white', {
        'pt-16': !showEpisodeHero,
      })}
    >
      {!showEpisodeHero && <Breadcrumb className="py-3" projectSlug={projectSlug} projectName={projectName} />}
      <PlayerMomentsProvider episodeGuid={episode.guid}>
        <CollectiblesPlayerEngagementProvider
          projectSlug={projectSlug}
          defaultEngagementDetail={defaultEngagementDetail}
          defaultMoment={defaultMoment}
          userStoryId={userStoryId}
        >
          {loading ? (
            <div className="aspect-h-9 aspect-w-16 relative flex h-full w-full items-center justify-center bg-core-gray-950 lg:h-[75vh]" />
          ) : (
            <Switch>
              <Case condition={showEpisodeHero}>
                <WatchEpisodeHero
                  loading={loading}
                  data={createCompatibleEpisodeResponse(data)}
                  episode={episode}
                  hasPayItForward={hasPayItForward}
                  slug={projectSlug}
                  isGeoBlocked={isGeoBlocked}
                  isEpisodeEarlyAccess={isEpisodeEarlyAccess}
                  isLoginRequired={isLoginRequired}
                  contentEpisode={contentEpisode}
                />
              </Case>
              <Case condition={showPlayer}>
                <CollectiblesPlayer
                  data={createCompatibleEpisodeResponse(data)}
                  episode={episode}
                  contentEpisode={contentEpisode}
                  defaultEngagementDetail={defaultEngagementDetail}
                  shouldSaveWatchPosition
                  skipAttribution={skipAttribution}
                  skipAds={skipAds}
                  autoplay={shouldAutoplay}
                  frame={frame}
                  defaultCommunityView="off"
                  isLoginRequired={isLoginRequired}
                  userId={userId}
                  isSellingTickets={isSellingTickets}
                  watchableContent={watchableContent}
                  setShowShareModal={setShowShareModal}
                  hasSkipLanguagesAvailable={hasParentalControlsAvailable}
                  showParentalControlsModal={showParentalControlsModal}
                  setShowParentalControlsModal={setShowParentalControlsModal}
                  updateSkipPreference={updateSkipPreference}
                  showParentalControlsExperiment={showParentalControls}
                  nextContent={nextContent}
                  preferences={preferences}
                  onSaveAndWatch={handleSaveAndWatch}
                />
              </Case>
              <Default>
                <CollectiblesPlayer
                  data={createCompatibleEpisodeResponse(data)}
                  episode={episode}
                  contentEpisode={contentEpisode}
                  defaultEngagementDetail={defaultEngagementDetail}
                  shouldSaveWatchPosition
                  skipAttribution={skipAttribution}
                  skipAds={skipAds}
                  autoplay={shouldAutoplay}
                  frame={frame}
                  defaultCommunityView="off"
                  isLoginRequired={isLoginRequired}
                  userId={userId}
                  isSellingTickets={isSellingTickets}
                  watchableContent={watchableContent}
                  setShowShareModal={setShowShareModal}
                  hasSkipLanguagesAvailable={hasParentalControlsAvailable}
                  showParentalControlsModal={showParentalControlsModal}
                  setShowParentalControlsModal={setShowParentalControlsModal}
                  updateSkipPreference={updateSkipPreference}
                  showParentalControlsExperiment={showParentalControls}
                  nextContent={nextContent}
                  preferences={preferences}
                  onSaveAndWatch={handleSaveAndWatch}
                />
              </Default>
            </Switch>
          )}
          <OneToManyContentSharingModal
            projectSlug={episode.projectSlug}
            projectName={data?.project?.name ?? undefined}
            moviePosterPath={data?.project?.title?.portraitAngelImage?.cloudinaryPath ?? undefined}
            episodeId={episode.id}
            showShareModal={showShareModal}
            setShowShareModal={setShowShareModal}
            isGuildShareAvailable={data?.project?.title?.isGuildShareAvailable ?? undefined}
          />
        </CollectiblesPlayerEngagementProvider>
      </PlayerMomentsProvider>

      <div className="relative mt-8 flex flex-col gap-8 overflow-x-hidden bg-core-gray-950 pb-32">
        <LazyHydrate whenVisible>
          <div className="my-8 border-t border-core-gray-800 pt-8">
            <AppPromoSection overrideSpacing />
          </div>
        </LazyHydrate>

        <When condition={!canSeeGuildShareButton}>
          <LazyHydrate whenVisible>
            <PaddedContainer fluid>
              <ShareSection
                projectName={projectName}
                theatricalSlug={projectSlug}
                shareUrl={asPath}
                projectType={projectType}
                className="ml-0 flex flex-col !items-start bg-core-gray-950 pt-12 !text-left"
                headerClassName="photon-title-sm"
              />
            </PaddedContainer>
          </LazyHydrate>
        </When>

        {shopifyStore && (
          <LazyHydrate whenVisible>
            <HorizontalShopifyMerchScroller
              className="border-t border-core-gray-800 pt-8"
              headerClassName="mx-4 sm:mx-8 md:mx-12 xl:mx-16"
              storeUrl={shopifyStore.storeUrl ?? ''}
              accessToken={shopifyStore.accessToken ?? ''}
              handle={shopifyStore.handle ?? ''}
              storeHref={shopifyStore?.storeHref}
              analytics={merchAnalytics}
              slug={projectSlug}
              padding
            />
          </LazyHydrate>
        )}

        {projectType !== projectTypes.movie && projectType !== projectTypes.special && (
          <PaddedContainer fluid>
            <section ref={containerRef} className="w-full border-t border-core-gray-800 pt-8">
              <div className="episodes-tab-container flex h-full w-full flex-col lg:flex-row">
                <When condition={isSeriesOrPodcast}>
                  <div className="relative lg:w-full lg:min-w-[396px] lg:max-w-fit xl:min-w-[428px]">
                    <Sticky
                      activeClass="z-[998] opacity-1 relative active"
                      releasedClass="z-0 opacity-0"
                      className="opacity-1 relative w-full transition-opacity duration-300 ease-in-out"
                      top={stickyTop}
                      bottomBoundary=".episodes-tab-container"
                    >
                      <PillTabs
                        items={seasonTabs}
                        activeId={activeSeason?.node?.id}
                        onChange={(item) => {
                          const title = data?.project?.title
                          if (title?.__typename === 'ContentSeries') {
                            const match = title.seasons?.edges?.find((s) => s?.node?.id === item.id)
                            if (match) setActiveSeason(match as ContentSeasonEdge)
                          }
                        }}
                        ariaLabel={t('season_tabs_aria_label', 'Season tabs')}
                      />
                    </Sticky>
                  </div>
                </When>
                <div className="flex min-h-[80px] flex-col gap-y-10 pl-4 pr-3 md:min-h-[150px] md:gap-y-6 md:px-12 lg:min-h-[280px] lg:gap-y-10 lg:pr-16">
                  {medias?.map((media, i) => (
                    <div key={media?.guid}>
                      <MediaCard
                        className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
                        thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
                        {...media}
                        key={media.guid}
                        imgPriority={i <= 4}
                        includeHoverScale={false}
                        userHasEarlyAccess={userHasEarlyAccess}
                        hasWatchableReasons={false}
                        watchableAvailabilityStatus={media?.watchableAvailabilityStatus ?? null}
                        projectType={projectType}
                        theaterEpisodeCard
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </PaddedContainer>
        )}
      </div>
    </div>
  )
}
